import { clamp01 } from "@/utils/box-drawing-utils";
import {
  type CornerIndex,
  type FrameSize,
  isTooSmall,
  resizeFromCorner,
  translateBox,
} from "@/utils/oriented-box";
import { useRef, useState, type PointerEvent } from "react";

/** What a press grabbed: the box's body, or one of its corners. */
export type ReshapeTarget = "move" | CornerIndex;

/**
 * How far (screen px) the pointer must travel before a press on a box is a
 * drag. Below it the press is a click (select + zoom) and the box is left
 * exactly as it was, so selecting a SAM 2 box never marks it `box_edited`.
 */
const DRAG_SLOP_PX = 4;

interface Drag {
  target: ReshapeTarget;
  /** The image's on-screen rect, cached at the press (pan ignores the box). */
  rect: DOMRect;
  startClientX: number;
  startClientY: number;
  orig: number[][];
  /** Latest shape, read on release (avoids a stale closure). */
  next: number[][];
  moved: boolean;
}

interface Options {
  /** The box's committed points, normalized. */
  points: number[][];
  /** Whether a press may start a move/resize right now. */
  enabled: boolean;
  /** Saves the reshaped box, once, on release. */
  onCommit: (points: number[][]) => void;
}

/**
 * Move and corner-resize for a box on the query image, keeping a tilted box's
 * angle (see `oriented-box`). The box's own div must take `boxRef`, sitting
 * directly in the image's wrapper, and render at `livePoints ?? points`: the
 * drag draws from local state and writes the store only on release.
 */
export const useBoxReshape = ({ points, enabled, onCommit }: Options) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<Drag | null>(null);
  const [livePoints, setLivePoints] = useState<number[][] | null>(null);
  // The browser still fires a click when a drag ends on the box; it must not
  // select + zoom as if the labeler had clicked.
  const justDraggedRef = useRef(false);

  const beginDrag = (e: PointerEvent<HTMLElement>, target: ReshapeTarget) => {
    // A new press: a drag's click that never came can't swallow this one.
    justDraggedRef.current = false;
    if (!enabled) return;
    e.stopPropagation();
    const wrapper = boxRef.current?.parentElement;
    if (!wrapper) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      target,
      rect: wrapper.getBoundingClientRect(),
      startClientX: e.clientX,
      startClientY: e.clientY,
      orig: points,
      next: points,
      moved: false,
    };
    setLivePoints(points);
  };

  const onPointerMove = (e: PointerEvent<HTMLElement>) => {
    const d = dragRef.current;
    if (!d) return;
    e.stopPropagation();
    if (
      Math.hypot(e.clientX - d.startClientX, e.clientY - d.startClientY) >
      DRAG_SLOP_PX
    ) {
      d.moved = true;
    }
    const { rect } = d;
    const x = clamp01((e.clientX - rect.left) / rect.width);
    const y = clamp01((e.clientY - rect.top) / rect.height);
    if (d.target === "move") {
      const x0 = clamp01((d.startClientX - rect.left) / rect.width);
      const y0 = clamp01((d.startClientY - rect.top) / rect.height);
      d.next = translateBox(d.orig, x - x0, y - y0);
    } else {
      d.next = resizeFromCorner(d.orig, d.target, [x, y], frameOf(rect));
    }
    setLivePoints(d.next);
  };

  const endDrag = (e: PointerEvent<HTMLElement>) => {
    const d = dragRef.current;
    if (!d) return;
    e.stopPropagation();
    dragRef.current = null;
    setLivePoints(null);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // pointer already released
    }

    if (!d.moved) return; // a click: onClick handles selection
    justDraggedRef.current = true;
    // A box collapsed by the resize keeps its committed points.
    if (isTooSmall(d.next, frameOf(d.rect))) return;
    onCommit(d.next);
  };

  /** True (once) for the click a drag's release fires — ignore that click. */
  const consumeDragClick = () => {
    if (!justDraggedRef.current) return false;
    justDraggedRef.current = false;
    return true;
  };

  /** Pointer handlers for an element that grabs `target`. */
  const grab = (target: ReshapeTarget) => ({
    onPointerDown: (e: PointerEvent<HTMLElement>) => beginDrag(e, target),
    onPointerMove,
    onPointerUp: endDrag,
  });

  return { boxRef, livePoints, grab, consumeDragClick };
};

const frameOf = (rect: DOMRect): FrameSize => ({
  width: rect.width,
  height: rect.height,
});
